/**
 * 虎鸡虫棒棋游戏组件
 * 游戏状态管理、用户交互和AI逻辑
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { TigerChickenGameState, Position, TigerChickenMove, Player, GameMode, Difficulty } from '../types/game';
import { createInitialGameState } from '../utils/tigerChicken';
import { selectPiece, makeMove, undoMove, getAllPossibleMoves } from '../utils/tigerChicken';
import { getTigerChickenAIDecision } from '../utils/tigerChickenAI';
import TigerChickenBoard from './TigerChickenBoard';
import GameShareButton from './GameShareButton';

interface TigerChickenGameProps {
  gameMode: GameMode;
  difficulty?: Difficulty;
  onBack: () => void;
  onGameEnd: (winner: Player | null) => void;
  onBackToModeSelect?: () => void;
}

const TigerChickenGame: React.FC<TigerChickenGameProps> = ({
  gameMode,
  difficulty = 'medium',
  onBack,
  onGameEnd,
  onBackToModeSelect
}) => {
  const [gameState, setGameState] = useState<TigerChickenGameState>(() =>
    createInitialGameState(gameMode, difficulty)
  );
  const [showEndGameDialog, setShowEndGameDialog] = useState(false);
  const [dialogManuallyClosed, setDialogManuallyClosed] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const isAIProcessingRef = useRef(false);

  // 检查游戏是否结束
  const checkGameEnd = useCallback((state: TigerChickenGameState) => {
    const moves = getAllPossibleMoves(state, state.currentPlayer);

    // 检查是否有获胜移动（占领对方大本营）
    for (const move of moves) {
      const opponentHome = state.currentPlayer === 'black' ? state.whiteHome : state.blackHome;
      if (move.to.row === opponentHome.row && move.to.col === opponentHome.col) {
        return { isEnd: true, winner: state.currentPlayer, reason: `${state.currentPlayer === 'black' ? '黑方' : '白方'}占领对方大本营获胜！` };
      }
    }

    // 检查是否还有合法移动
    if (moves.length === 0) {
      return { isEnd: true, winner: null, reason: '无法移动，和棋！' };
    }

    return { isEnd: false, winner: null, reason: '' };
  }, []);

  // AI移动处理
  const handleAIMove = useCallback(async () => {
    // 检查是否应该执行AI移动（使用ref防止重复调用）
    if (gameMode !== 'pvc' || gameState.currentPlayer !== 'white' || gameState.status !== 'playing' || isAIProcessingRef.current) {
      return;
    }

    // 设置AI处理标志
    isAIProcessingRef.current = true;
    setGameState(prev => ({ ...prev, isAIThinking: true }));

    try {
      // 模拟AI思考时间
      await new Promise(resolve => setTimeout(resolve, 800));

      // 获取当前游戏状态进行AI决策
      const aiMove = getTigerChickenAIDecision(gameState, difficulty);

      if (aiMove) {
        // 执行AI移动
        setGameState(prev => {
          const newState = makeMove(prev, aiMove);
          const gameEnd = checkGameEnd(newState);

          if (gameEnd.isEnd) {
            isAIProcessingRef.current = false;
            return {
              ...newState,
              status: 'finished',
              winner: gameEnd.winner,
              isAIThinking: false
            };
          }

          isAIProcessingRef.current = false;
          return {
            ...newState,
            isAIThinking: false
          };
        });
      } else {
        // 没有找到可用移动，结束AI思考
        isAIProcessingRef.current = false;
        setGameState(prev => ({ ...prev, isAIThinking: false }));
      }
    } catch (error) {
      console.error('AI移动错误:', error);
      isAIProcessingRef.current = false;
      setGameState(prev => ({ ...prev, isAIThinking: false }));
    }
  }, [gameMode, gameState.currentPlayer, gameState.status, gameState, difficulty, checkGameEnd]);

  // 处理格子点击
  const handleCellClick = useCallback((position: Position) => {
    if (gameState.status !== 'playing' || gameState.isAIThinking) {
      return;
    }

    // 如果是AI模式且当前玩家是AI，则不允许点击
    if (gameMode === 'pvc' && gameState.currentPlayer === 'white') {
      return;
    }

    const clickedPiece = gameState.board[position.row][position.col];
    const selectedPiece = gameState.selectedPiece;

    // 如果点击了当前玩家的棋子
    if (clickedPiece && clickedPiece.player === gameState.currentPlayer) {
      const newState = selectPiece(gameState, position);
      setGameState(newState);
      return;
    }

    // 如果已经选中了棋子，尝试移动
    if (selectedPiece) {
      const possibleMove = gameState.possibleMoves?.find(
        move => move.to.row === position.row && move.to.col === position.col
      );

      if (possibleMove) {
        // 执行移动
        setGameState(prev => {
          const newState = makeMove(prev, possibleMove);
          const gameEnd = checkGameEnd(newState);

          if (gameEnd.isEnd) {
            return {
              ...newState,
              status: 'finished',
              winner: gameEnd.winner
            };
          }

          return newState;
        });
      } else {
        // 取消选择
        setGameState(prev => ({ ...prev, selectedPiece: undefined, possibleMoves: [] }));
      }
    }
  }, [gameState, gameMode, checkGameEnd]);

  // 悔棋功能
  const handleUndo = useCallback(() => {
    if (gameState.gameHistory.length > 0 && gameState.status === 'playing' && !gameState.isAIThinking) {
      const newState = undoMove(gameState);
      setGameState(newState);
    }
  }, [gameState]);

  // 重新开始游戏
  const handleRestart = useCallback(() => {
    isAIProcessingRef.current = false;
    setGameState(createInitialGameState(gameMode, difficulty));
    setShowEndGameDialog(false);
    setDialogManuallyClosed(false);
  }, [gameMode, difficulty]);

  // 监听游戏结束
  useEffect(() => {
    if (gameState.status === 'finished' && !showEndGameDialog && !dialogManuallyClosed) {
      setShowEndGameDialog(true);
      onGameEnd(gameState.winner);
    }
  }, [gameState.status, gameState.winner, showEndGameDialog, dialogManuallyClosed, onGameEnd]);

  // AI移动触发
  useEffect(() => {
    // 只在轮到AI且游戏正在进行时触发AI移动
    if (gameMode === 'pvc' && gameState.currentPlayer === 'white' && gameState.status === 'playing' && !gameState.isAIThinking && !isAIProcessingRef.current) {
      handleAIMove();
    }
  }, [gameState.currentPlayer, gameMode, gameState.status, gameState.isAIThinking, handleAIMove]);

  // 更新悔棋按钮状态
  useEffect(() => {
    setCanUndo(gameState.gameHistory.length > 0 && gameState.status === 'playing' && !gameState.isAIThinking);
  }, [gameState.gameHistory.length, gameState.status, gameState.isAIThinking]);

  // 游戏结束对话框
  const EndGameDialog = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl relative">
        {/* 关闭按钮 */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowEndGameDialog(false);
            setDialogManuallyClosed(true);
          }}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
          title="关闭"
        >
          ×
        </button>
        <h2 className="text-2xl font-bold text-center mb-4">
          {gameState.winner ? (
            <span className="text-green-600">
              {gameState.winner === 'black' ? '黑方' : '白方'}获胜！
            </span>
          ) : (
            <span className="text-yellow-600">和棋！</span>
          )}
        </h2>

        <div className="text-center text-gray-600 mb-6">
          <p>游戏统计：</p>
          <p>总步数: {gameState.totalMoves}</p>
          <p>吃子数: {gameState.captureMoves}</p>
        </div>

        <div className="flex space-x-4">
          <button
            onClick={handleRestart}
            className="flex-1 bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors font-medium"
          >
            再来一局
          </button>
          <button
            onClick={onBack}
            className="flex-1 bg-gray-500 text-white px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors font-medium"
          >
            返回主页
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex flex-col">
      {/* 游戏头部 - 统一布局 */}
      <div className="bg-white shadow p-2">
        {/* 标题区域 - 手机端独立行，桌面端居中 */}
        <div className="text-center mb-2 sm:mb-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center sm:space-x-0 sm:space-y-1 space-y-1">
            {/* 美化标题 */}
            <div className="flex items-center justify-center space-x-2">
              <span className="text-2xl">🐅</span>
              <h1 className="text-base sm:text-lg font-bold bg-gradient-to-r from-red-600 via-orange-600 to-yellow-600 bg-clip-text text-transparent sm:mb-1">
                虎鸡虫棒棋
              </h1>
              <span className="text-2xl">🥢</span>
            </div>

            {/* 美化模式描述 */}
            <div className="flex items-center justify-center space-x-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-indigo-500 to-purple-500 text-white">
                <span className="w-2 h-2 mr-1 bg-white rounded-full animate-pulse"></span>
                {gameMode === 'pvp' ? '双人对战' : `人机对战`}
              </span>

              {gameMode === 'pvc' && (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                  difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {difficulty === 'easy' ? '😊 简单' : difficulty === 'medium' ? '🎯 中等' : '🔥 困难'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* 控制按钮区域 */}
        <div className="flex items-center justify-between">
          {/* 左侧按钮组 */}
          <div className="flex items-center space-x-1 sm:space-x-1">
            {/* 首页按钮 */}
            <button onClick={onBack} className="flex items-center space-x-1 px-2 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-xs font-medium" title="回到首页">
              <span>🏠</span>
              <span className="hidden sm:inline">首页</span>
            </button>
            {/* 返回按钮 */}
            {onBackToModeSelect && (
              <button onClick={onBackToModeSelect} className="flex items-center space-x-1 px-2 py-1.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-xs font-medium" title="返回模式选择">
                <span>🔙</span>
                <span className="hidden sm:inline">返回</span>
              </button>
            )}

            {/* 重新开始按钮 */}
            <button onClick={handleRestart} className="flex items-center space-x-1 px-2 py-1.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-xs font-medium" title="重新开始">
              <span>🔄</span>
              <span className="hidden sm:inline">重开</span>
            </button>

            {/* 悔棋按钮 */}
            <button onClick={handleUndo} disabled={!canUndo} className={`flex items-center space-x-1 px-2 py-1.5 rounded-lg transition-colors text-xs font-medium ${
              canUndo ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`} title="悔棋">
              <span>↩️</span>
              <span className="hidden sm:inline">悔棋</span>
            </button>
          </div>

          {/* 右侧控制按钮 */}
          <div className="flex items-center space-x-1 sm:space-x-1">
            {/* 游戏规则按钮 */}
            <button onClick={() => setShowRules(true)} className="flex items-center space-x-1 px-2 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-xs font-medium" title="游戏规则">
              <span>📖</span>
              <span className="hidden sm:inline">规则</span>
            </button>

            {/* 分享按钮 */}
            <GameShareButton gameType="tigerchicken" />
          </div>
        </div>
      </div>

      {/* 游戏状态信息 - 统一布局 */}
      {gameState.status === 'playing' && (
        <div className="bg-white shadow p-2">
          <div className="flex items-center justify-between">
            {/* 当前玩家信息 */}
            <div className="flex items-center space-x-1 sm:space-x-1">
              <div className={`w-4 h-4 rounded-full ${
                gameState.currentPlayer === 'black' ? 'bg-gray-900' : 'bg-gray-100 border-2 border-gray-400'
              }`}></div>
              <span className="text-sm sm:text-base font-medium">
                {gameState.currentPlayer === 'black' ? '黑方' : '白方'}回合
              </span>
              {gameState.isAIThinking && (
                <span className="text-xs text-blue-600 animate-pulse ml-2">AI思考中...</span>
              )}
            </div>

            {/* 步数信息 */}
            <div className="flex items-center space-x-3 text-sm sm:text-base text-gray-600">
              <span>步数: {gameState.totalMoves}</span>
              <span>吃子: {gameState.captureMoves}</span>
            </div>
          </div>
        </div>
      )}

      {/* 棋盘 - 移动端满屏布局 */}
      <div className="flex-1 flex items-center justify-center px-1 py-1 xs:px-1 xs:py-2">
        <div className="w-full h-full max-w-none">
          <TigerChickenBoard
            gameState={gameState}
            onCellClick={handleCellClick}
            disabled={gameState.status !== 'playing' || gameState.isAIThinking}
            gameMode={gameMode}
          />
        </div>
      </div>

      {/* 游戏规则提示 - 统一布局 */}
      <div className="bg-blue-50 border-t border-blue-200 p-2 sm:p-3">
        <p className="text-sm sm:text-base text-blue-800 text-center leading-relaxed">
          <strong>规则：</strong>
          虎吃鸡 &gt; 鸡啄虫 &gt; 虫蛀棒 &gt; 棒打虎 | 一步一格 | 对角线移动 | 占领对方大本营获胜 | {gameMode === 'pvc' ? '人机对战' : '双人对战'}
        </p>
      </div>

      {/* 游戏规则对话框 */}
      {showRules && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto relative">
            {/* 关闭按钮 */}
            <button
              onClick={() => setShowRules(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
              title="关闭"
            >
              ×
            </button>
            <h2 className="text-2xl font-bold text-center mb-4 text-gray-800 pr-8">
              🐅 虎鸡虫棒游戏规则
            </h2>

            <div className="text-gray-700 space-y-4 mb-6">
              <div>
                <h3 className="font-bold text-lg mb-2">游戏目标</h3>
                <p>占领对方大本营或使对方无法移动获胜！</p>
              </div>

              <div>
                <h3 className="font-bold text-lg mb-2">相克关系</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>🐅 虎 → 🐔 鸡 （虎吃鸡）</li>
                  <li>🐔 鸡 → 🐛 虫 （鸡啄虫）</li>
                  <li>🐛 虫 → 🪵 棒 （虫蛀棒）</li>
                  <li>🪵 棒 → 🐅 虎 （棒打虎）</li>
                  <li>形成循环相克，没有绝对最强的棋子</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-lg mb-2">移动规则</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>棋子每次只能移动一格</li>
                  <li>可以向8个方向移动（上下左右+对角线）</li>
                  <li>不能移动到已有己方棋子的位置</li>
                  <li>可以移动到对方棋子的位置进行吃子</li>
                  <li>只能吃掉自己相克的对方棋子</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-lg mb-2">特殊规则</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li><strong>大本营：</strong>棋盘两端的安全区域</li>
                  <li><strong>获胜条件1：</strong>任意己方棋子占领对方大本营</li>
                  <li><strong>获胜条件2：</strong>对方所有棋子无法移动</li>
                  <li><strong>和棋条件：</strong>双方都无法移动</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-lg mb-2">策略提示</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>保持棋子分布，避免被一网打尽</li>
                  <li>保护后方，防止对手偷袭大本营</li>
                  <li>合理利用相克关系，选择有利时机进攻</li>
                  <li>注意对手的威胁棋子，及时防守</li>
                  <li>占据中心位置，增加移动选择</li>
                </ul>
              </div>
            </div>

            <button
              onClick={() => setShowRules(false)}
              className="w-full bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors font-medium"
            >
              我知道了
            </button>
          </div>
        </div>
      )}

      {/* 游戏结束对话框 */}
      {showEndGameDialog && <EndGameDialog />}
    </div>
  );
};

export default TigerChickenGame;